import {useState, useEffect, useRef} from "react";

import {useReduxData} from "../../../customHooks/useContextReduxData";
import {getUserFriendlyTime} from "./utils/timeConverter";
import PlotPointer from "./PlotPointer";
import hintModeTooltipHOC from "../../../onboarding/hintModeTooltipHOC";


const PLOT_DIVISION_COUNT = 50;

const frameIntervals = [0.5, 1, 3, 5, 10, 20, 25];
const fpsOptions = [0.25, 0.5, 1, 2, 4, 8, 16];


export default hintModeTooltipHOC(function() {
    const [points, setPoints] = useState({});
    const [maxFrequency, setMaxFrequency] = useState(0);
    const [maxFrequencyCoo, setMaxFrequencyCoo] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [plotTop, setPlotTop] = useState(0);
    const [plotLeft, setPlotLeft] = useState(0);
    const [plotHeight, setPlotHeight] = useState(0);
    const [plotWidth, setPlotWidth] = useState(0);
    const [currentTime, setCurrentTime] = useState(1);
    const [valueOfDivision, setValueOfDivision] = useState(1);

    const [videoFrameInterval, setVideoFrameInterval] = useState(1);
    const [videoFPS, setVideoFPS] = useState(8);

    const videoInterval = useRef();
    const plotRef = useRef();

    const {actionHistory, isWatchMode} = useReduxData();

    useEffect(() => {
        if (plotRef?.current) {
            const {width, height, top, left} = plotRef.current.getBoundingClientRect();
            if (height && height !== plotHeight) {
                setPlotHeight(height);
            }
            if (width && width !== plotWidth) {
                setPlotWidth(width);
            }
            if (top && top !== plotTop) {
                setPlotTop(top);
            }
            if (left && left !== plotLeft) {
                setPlotLeft(left);
            }
        }
    }, [plotRef, maxFrequency]);

    useEffect(() => {
        if (isWatchMode && plotLeft && plotTop && plotWidth && plotHeight) {
            const xValueOfDivision = Math.round((plotWidth - 5) / PLOT_DIVISION_COUNT);
            const rawLocalPoints = {};
            let maxPointX = 0;
            actionHistory.map(action => {
                const time = action.time;
                if (time) {
                    rawLocalPoints[time] = (rawLocalPoints[time] || 0) + 1;
                    if (maxPointX < time) {
                        maxPointX = time;
                    }
                }
            });
            const localPoints = {};
            let maxPointY = 0;
            Object.keys(rawLocalPoints).map(xCoo => {
                const newX = Math.round(
                    Math.floor((xCoo / maxPointX * (plotWidth - 5)) / xValueOfDivision) * xValueOfDivision
                );
                localPoints[newX] = (localPoints[newX] || 0) + rawLocalPoints[xCoo];
                if (maxPointY < localPoints[newX]) {
                    maxPointY = localPoints[newX];
                }
            });
            let maxFreqCoo = Number.POSITIVE_INFINITY;
            Object.keys(localPoints).map(xCoo => {
                localPoints[xCoo] = Math.round(-localPoints[xCoo] / maxPointY * plotHeight / 1.1 + plotHeight)
                if (localPoints[xCoo] < maxFreqCoo) {
                    maxFreqCoo = localPoints[xCoo];
                }
            });
            setPoints(localPoints);
            setMaxTime(Math.round(maxPointX / 1000));
            setMaxFrequency(maxPointY);
            setMaxFrequencyCoo(maxFreqCoo);
            setValueOfDivision(xValueOfDivision);
        }
    }, [actionHistory, isWatchMode, plotLeft, plotTop, plotWidth, plotHeight]);

    const setNewFrameInVideo = () => {
        setCurrentTime(
            (prev) => {
                if (prev + maxTime * videoFrameInterval / 100 < maxTime) {
                    return prev + maxTime * videoFrameInterval / 100;
                } else {
                    clearInterval(videoInterval.current);
                    return maxTime;
                }
            }
        );
    }

    const playVideo = () => {
        videoInterval.current = setInterval(
            setNewFrameInVideo,
            1 / videoFPS * 1000,
        );
    }

    const pauseVideo = () => {
        console.log('stop');
        clearInterval(videoInterval.current);
    }

    const resetVideo = () => {
        clearInterval(videoInterval.current);
        setCurrentTime(0);
    }

    const changeVideoFrameInterval = event => {
        setVideoFrameInterval(event.target.value);
    }

    const changeVideoFPS = event => {
        setVideoFPS(event.target.value);
    }

    return <div className="figure-bar-action-history-container">
        <div className="figure-bar-action-history-plot-container">
            <span className="figure-bar-action-history-max-frequency">{maxFrequency}</span>
            <svg className="figure-bar-action-history-plot" ref={plotRef} width="97%">
                <line
                    x1="0"
                    x2="100000"
                    y1={maxFrequencyCoo}
                    y2={maxFrequencyCoo}
                    stroke="black"
                    strokeDasharray="5"
                    style={{pointerEvents: "none"}}
                />
                <PlotPointer
                    plotHeight={plotHeight}
                    plotLeft={plotLeft}
                    plotWidth={plotWidth}
                    setCurrentTime={setCurrentTime}
                    currentTime={currentTime}
                />
                {Object.keys(points).map(xCoo =>
                    <line
                        x1={+xCoo}
                        x2={+xCoo + valueOfDivision}
                        y1={points[xCoo]}
                        y2={points[xCoo]}
                        stroke="black"
                        strokeWidth="0.25rem"
                        style={{pointerEvents: "none"}}
                    />
                )}
            </svg>
        </div>
        <span className="figure-bar-action-history-time-info">
            {getUserFriendlyTime(currentTime) + " / " + getUserFriendlyTime(maxTime)}
            <input type='button' className='figure-bar-action-history-plot-control' value="Старт" onClick={playVideo}/>
            <input type='button' className='figure-bar-action-history-plot-control' value="Стоп" onClick={pauseVideo}/>
            <input type='button' className='figure-bar-action-history-plot-control' value="Сброс" onClick={resetVideo}/>
            <select className="postcard-config-text-color" value={videoFrameInterval} onChange={changeVideoFrameInterval}>
                {frameIntervals.map(item => <option
                    key={`frame_interval-${item}`}
                    value={item}
                >Шаг {item} %</option>)}
            </select>
            <select className="postcard-config-text-color" value={videoFPS} onChange={changeVideoFPS}>
                {fpsOptions.map(item => <option
                    key={`fps-${item}`}
                    value={item}
                >{item} FPS</option>)}
            </select>
        </span>
    </div>;
}, "timeSlider", {height: "100%", width: "100%"});
